import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  CheckCircle, XCircle, ArrowRight, ArrowLeft, 
  Award, RefreshCw, Home, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuizPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logEvent } = useAuth();
  
  const [parcours, setParcours] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previousResult, setPreviousResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id, user]);

  const fetchData = async () => {
    try {
      const [parcoursRes, resultRes] = await Promise.all([
        axios.get(`${API}/parcours/${id}`),
        user ? axios.get(`${API}/quiz/result/${user.id}/${id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null })
      ]);
      setParcours(parcoursRes.data);
      setPreviousResult(resultRes.data);
      setAnswers(new Array(parcoursRes.data.quiz?.length || 5).fill(-1));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement du quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < parcours.quiz.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    parcours.quiz.forEach((q, i) => {
      if (answers[i] === q.correct) {
        correct++;
      }
    });
    return Math.round((correct / parcours.quiz.length) * 100);
  };

  const handleSubmit = async () => {
    const calculatedScore = calculateScore();
    setScore(calculatedScore);
    setSubmitting(true);

    try {
      await axios.post(`${API}/quiz/submit`, {
        user_id: user.id,
        parcours_id: id,
        answers,
        score: calculatedScore
      });

      logEvent(calculatedScore >= 75 ? 'quiz_validated' : 'quiz_attempted', id, null, { score: calculatedScore });
      
      if (calculatedScore >= 75) {
        toast.success('Félicitations ! Quiz validé !');
      } else {
        toast.warning(`Score insuffisant (${calculatedScore}%). Minimum requis : 75%`);
      }
      
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Erreur lors de la soumission du quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setAnswers(new Array(parcours.quiz?.length || 5).fill(-1));
    setShowResults(false);
    setScore(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]">
        <Navbar />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#cc5759]" />
        </div>
      </div>
    );
  }

  if (!parcours || !parcours.quiz) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]">
        <Navbar />
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-[#571925]">Quiz non disponible</h1>
          <Button onClick={() => navigate(`/vipplus/parcours/${id}`)} className="mt-4">
            Retour au parcours
          </Button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / parcours.quiz.length) * 100;
  const allAnswered = answers.every(a => a !== -1);

  // Results View
  if (showResults) {
    const passed = score >= 75;
    
    return (
      <div className="min-h-screen bg-[#f2f2f2]" data-testid="quiz-results">
        <Navbar />
        
        <div className="container mx-auto px-4 md:px-8 py-12 max-w-3xl">
          <Card className="text-center">
            <CardContent className="py-12">
              {/* Result Icon */}
              <div className={`h-24 w-24 rounded-full mx-auto mb-6 flex items-center justify-center ${passed ? 'bg-green-100' : 'bg-red-100'}`}>
                {passed ? (
                  <Award className="h-12 w-12 text-green-600" />
                ) : (
                  <XCircle className="h-12 w-12 text-red-600" />
                )}
              </div>

              {/* Score */}
              <h2 className="text-3xl font-bold mb-2 font-['Outfit']">
                {passed ? 'Félicitations !' : 'Encore un effort !'}
              </h2>
              <p className="text-gray-600 mb-6">
                {passed 
                  ? 'Vous avez validé ce module avec succès.'
                  : 'Le seuil de validation est de 75%. Réessayez !'
                }
              </p>

              <div className="text-6xl font-bold mb-2" style={{ color: passed ? '#22c55e' : '#ef4444' }}>
                {score}%
              </div>
              <p className="text-gray-500 mb-8">
                {answers.filter((a, i) => a === parcours.quiz[i].correct).length} / {parcours.quiz.length} réponses correctes
              </p>

              {/* Answers Review */}
              <div className="text-left mb-8 space-y-4">
                <h3 className="font-semibold text-[#1a1a1a] mb-4">Détail des réponses :</h3>
                {parcours.quiz.map((q, i) => {
                  const isCorrect = answers[i] === q.correct;
                  return (
                    <div key={i} className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-start gap-3">
                        {isCorrect ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                        )}
                        <div>
                          <p className="font-medium text-[#1a1a1a]">{q.question}</p>
                          <p className="text-sm mt-1">
                            <span className="text-gray-600">Votre réponse : </span>
                            <span className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                              {q.options[answers[i]] || 'Non répondu'}
                            </span>
                          </p>
                          {!isCorrect && (
                            <p className="text-sm text-green-700 mt-1">
                              Bonne réponse : {q.options[q.correct]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!passed && (
                  <Button 
                    onClick={handleRetry}
                    className="bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full"
                    data-testid="retry-quiz-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Réessayer le quiz
                  </Button>
                )}
                <Button 
                  onClick={() => navigate(`/vipplus/parcours/${id}`)}
                  variant="outline"
                  className="border-[#571925] text-[#571925] hover:bg-[#571925] hover:text-white rounded-full"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour au parcours
                </Button>
                {passed && (
                  <Button 
                    onClick={() => navigate(`/vipplus/feedback/satisfaction?parcours=${id}`)}
                    className="bg-[#571925] hover:bg-[#3d1119] text-white rounded-full"
                    data-testid="satisfaction-btn"
                  >
                    Donner mon avis
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz View
  const currentQ = parcours.quiz[currentQuestion];

  return (
    <div className="min-h-screen bg-[#f2f2f2]" data-testid="quiz-page">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#571925] mb-2 font-['Outfit']">
            Quiz : {parcours.title}
          </h1>
          <p className="text-gray-600">
            Répondez aux 5 questions pour valider vos acquis. Seuil de validation : 75%
          </p>
          {previousResult && (
            <p className="text-sm text-[#cc5759] mt-2">
              Tentative précédente : {previousResult.score}% ({previousResult.attempts} essai{previousResult.attempts > 1 ? 's' : ''})
            </p>
          )}
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Question {currentQuestion + 1} sur {parcours.quiz.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg text-[#1a1a1a]">
              {currentQ.question}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={answers[currentQuestion]?.toString()} 
              onValueChange={(value) => handleAnswer(currentQuestion, parseInt(value))}
            >
              <div className="space-y-3">
                {currentQ.options.map((option, i) => (
                  <div 
                    key={i}
                    className={`quiz-option flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      answers[currentQuestion] === i 
                        ? 'border-[#ff9a52] bg-[#ff9a52]/10' 
                        : 'border-gray-200 hover:border-[#cc5759]'
                    }`}
                    onClick={() => handleAnswer(currentQuestion, i)}
                    data-testid={`option-${i}`}
                  >
                    <RadioGroupItem value={i.toString()} id={`option-${i}`} />
                    <Label htmlFor={`option-${i}`} className="flex-1 cursor-pointer text-[#1a1a1a]">
                      {option}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="border-[#571925] text-[#571925] hover:bg-[#571925] hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Précédent
          </Button>

          {/* Question Indicators */}
          <div className="flex gap-2">
            {parcours.quiz.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQuestion(i)}
                className={`h-3 w-3 rounded-full transition-colors ${
                  i === currentQuestion 
                    ? 'bg-[#ff9a52]' 
                    : answers[i] !== -1 
                      ? 'bg-[#571925]' 
                      : 'bg-gray-300'
                }`}
                data-testid={`question-indicator-${i}`}
              />
            ))}
          </div>

          {currentQuestion === parcours.quiz.length - 1 ? (
            <Button 
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="bg-[#ff9a52] hover:bg-[#e08540] text-white"
              data-testid="submit-quiz-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  Valider
                  <CheckCircle className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleNext}
              className="bg-[#ff9a52] hover:bg-[#e08540] text-white"
            >
              Suivant
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {!allAnswered && currentQuestion === parcours.quiz.length - 1 && (
          <p className="text-center text-sm text-[#cc5759] mt-4">
            Veuillez répondre à toutes les questions avant de valider.
          </p>
        )}
      </div>
    </div>
  );
};
